import { lazy } from 'react';

// project imports
import Loadable from 'ui-component/Loadable';
import { Navigate } from 'react-router';
import VerificationPage from 'views/dashboard/VerificationPage';
import EmailTracker from 'views/dashboard/EmailTracker';
import LeadGen from 'views/dashboard/LeadGen';
import LeadGenLayout from 'layout/LeadGenLayout';
import LeadGenData from 'views/dashboard/LeadGenData';
import LeadGenDataLayout from 'layout/LeadGenLayout/LeadGenDataLayout.js';
import LeadGenStaffCard from 'views/dashboard/LeadGenStaffCard';
import LeadGenImporter from 'views/dashboard/LeadGenImporter';
import SponsorExhibitorImporter from 'views/dashboard/SponsorExhibitorImporter';
import ShowSponsorsListLayout from 'layout/ShowSponsorsListLayout';
import SponsorsReg from 'views/dashboard/SponsorsReg';
import CorporatesReg from 'views/dashboard/CorporatesReg';
import SponsorRegList from 'views/dashboard/SponsorRegList';
import CorporateRegList from 'views/dashboard/CorporateRegList';
import RoleSwitcher from 'views/dashboard/RoleSwitcher';
import BulkPrinting from 'views/dashboard/BulkPrinting';
import ExhibitorsReg from 'views/dashboard/ExhibitorsReg';
import ExhibitorRegList from 'views/dashboard/ExhibitorRegList';
import CouponManager from 'views/dashboard/CouponManager';
import ManageTheme from 'views/dashboard/ManageTheme';
import ThemeDetail from 'views/dashboard/ThemeDetail';
import UnifiedEmailTemplateManager from 'views/dashboard/UnifiedEmailTemplateManager';
import AuditLogDashboard from 'views/dashboard/AuditLogDashboard';

// dashboard routing - with enhanced analytics
const Dashboard = Loadable(lazy(() => import('views/dashboard/Dashboard')));
const EnhancedDashboard = Loadable(lazy(() => import('views/dashboard/EnhancedDashboard')));
const ComprehensiveDashboard = Loadable(lazy(() => import('views/dashboard/ComprehensiveDashboard')));
const AnalyticsDashboard = Loadable(lazy(() => import('views/dashboard/AnalyticsDashboard')));
const GateScanPage = Loadable(lazy(() => import('views/dashboard/GateScanPage')));
const PrintPage = Loadable(lazy(() => import('views/dashboard/PrintPage')));
const RegistrationForm = Loadable(lazy(() => import('views/dashboard/RegistrationForm')));
const RegDashboard = Loadable(lazy(() => import('views/dashboard/Dashboard')));
const DashLayout = Loadable(lazy(() => import('layout/DashLayout/index')));
const MinimalLayout = Loadable(lazy(() => import('layout/MinimalLayout/index')));
const Crew = Loadable(lazy(() => import('views/dashboard/Crew')));
const DataImportPage = Loadable(lazy(() => import('views/dashboard/DataImporter')));
const RegistrationPage = Loadable(lazy(() => import('views/dashboard/RegistrationPage')));
const LogoutPage = Loadable(lazy(() => import('views/pages/Logout')));
const RolePage = Loadable(lazy(() => import('views/dashboard/RolePage')));
const RoleLayout = Loadable(lazy(() => import('layout/RoleLayout/index')));
const MainLayout = Loadable(lazy(() => import('layout/MainLayout/index')));
const SocialMarketingPage = Loadable(lazy(() => import('views/dashboard/SocialMarketingPage')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '',
    element: <MinimalLayout />,
    children: [
        {
            path: '',
            element: <Navigate to='logout' replace />,
        },
        {
            path: 'logout',
            element: <LogoutPage />,
        },
        {
            path: ':eventId',
            element: <MainLayout />,
            children: [
                {
                    path: '',
                    element: <Navigate to='dashboard' replace />,
                },
                {
                    path: 'dashboard',
                    element: <DashLayout />,
                    children: [
                        {
                            path: '',
                            element: <EnhancedDashboard />,
                        },

                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: ':role',
                    element: <RoleLayout />,
                    children: [
                        {
                            path: '',
                            element: <Navigate to='data' replace />,
                        },
                        {
                            path: 'data',
                            element: <RolePage />,
                        },
                        {
                            path: 'registration',
                            element: <RegistrationForm />,
                        },
                        {
                            path: 'data-import',
                            element: <DataImportPage />,
                        },
                        {
                            path: 'social-marketing',
                            element: <SocialMarketingPage />,
                        },
                        {
                            path: 'manage-theme',
                            element: <ManageTheme />,
                        },
                        {
                            path: 'manage-theme/:themeId',
                            element: <ThemeDetail />,
                        },
                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: 'crew',
                    element: <Crew />,
                },
                {
                    path: 'crew/RECEPTION',
                    element: <Crew />,
                },
                {
                    path: 'roleSwitcher',
                    element: <RoleSwitcher />,
                },
                {
                    path: 'bulkprinting',
                    element: <BulkPrinting />,
                },
                {
                    path: 'crew/PRINTER',
                    element: <Crew />,
                },
                {
                    path: 'crew/ENTRY_SCAN',
                    element: <Crew />,
                },
                {
                    path: 'crew/EXIT_SCAN',
                    element: <Crew />,
                },
                {
                    path: 'verify/:role',
                    element: <VerificationPage />,
                },
                {
                    path: 'emailtracking',
                    element: <EmailTracker />,
                },
                {
                    path: 'email-templates',
                    element: <UnifiedEmailTemplateManager />,
                },
                {
                    path: 'audit-logs',
                    element: <AuditLogDashboard />,
                },
                {
                    path: 'leadgeneration',
                    element: <LeadGenLayout />,
                    children: [
                        {
                            path: '',
                            element: <LeadGen />,
                        },
                        {
                            path: 'import',
                            element: <LeadGenImporter />,
                        },
                        {
                            path: ':exhibitorId',
                            element: <LeadGenDataLayout />,
                            children: [
                                {
                                    path: '',
                                    element: <LeadGenData />,
                                },
                                {
                                    path: ':staffId',
                                    element: <LeadGenStaffCard />,
                                },
                                {
                                    path: '*',
                                    element: <LogoutPage />,
                                },
                            ],
                        },
                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: 'showsponsors',
                    element: <ShowSponsorsListLayout />,
                    children: [
                        {
                            path: '',
                            element: <SponsorsReg />,
                        },
                        {
                            path: 'import',
                            element: <SponsorExhibitorImporter importRole='Sponsors' />,
                        },
                        {
                            path: ':sponsorId',
                            element: <SponsorRegList />,
                        },
                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: 'showcorporates',
                    element: <ShowSponsorsListLayout />,
                    children: [
                        {
                            path: '',
                            element: <CorporatesReg />,
                        },
                        {
                            path: 'import',
                            element: <SponsorExhibitorImporter importRole='Corporates' />,
                        },
                        {
                            path: ':corporateId',
                            element: <CorporateRegList />,
                        },
                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: 'showexhibitors',
                    element: <ShowSponsorsListLayout />,
                    children: [
                        {
                            path: '',
                            element: <ExhibitorsReg />,
                        },
                        {
                            path: 'import',
                            element: <SponsorExhibitorImporter importRole='Exhibitors' />,
                        },
                        {
                            path: ':corporateId',
                            element: <ExhibitorRegList />,
                        },
                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: 'generatePromo',
                    element: <ShowSponsorsListLayout />,
                    children: [
                        {
                            path: '',
                            element: <CouponManager />,
                        },
                        {
                            path: '*',
                            element: <LogoutPage />,
                        },
                    ],
                },
                {
                    path: 'logout',
                    element: <LogoutPage />,
                },
            ],
        },
        {
            path: '*',
            element: <LogoutPage />,
        },
    ],
};

export default MainRoutes;
